// Svelte-action ленивого рендера картинок ленты (этап 13.4: память на iPhone).
//
// use:lazyImage={{ src }} на <img> БЕЗ атрибута src. Реальный data URL подставляется,
// только пока картинка в пределах полутора высот скроллера от его видимой области
// (скроллер — ближайший предок [data-scroll-root], иначе окно); дальше — прозрачный
// 1×1 gif. Геометрию держит CSS (.message-image: width 100% + aspect-ratio + max-height),
// поэтому высота ленты от подмены не меняется, а декодированный растр WebKit освобождает.
// Зачем: лента из 30 фото по 1600px держит ~250 МБ растров; на iPhone с 4 ГБ процесс
// WebContent убивают по памяти, и PWA «перезагружается» (заметно при свайпе, когда
// экран целиком уходит в композитные слои). Без IntersectionObserver (jsdom, старые
// браузеры) — обычный <img> с src сразу.

export const LAZY_IMAGE_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
export const LAZY_IMAGE_ROOT_MARGIN = '150% 0px';
const NEAR_VIEWPORT_RATIO = 1.5;

// Один наблюдатель на скроллер: сотни картинок в ленте не плодят сотни observer'ов.
const observers = new Map(); // root (Element | null) → { observer, handlers: Map<node, fn> }

function observerFor(root) {
  let entry = observers.get(root);
  if (!entry) {
    const handlers = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const item of entries) {
          const handler = handlers.get(item.target);
          if (handler) {
            handler(item.isIntersecting);
          }
        }
      },
      { root, rootMargin: LAZY_IMAGE_ROOT_MARGIN, threshold: 0 }
    );
    entry = { observer, handlers };
    observers.set(root, entry);
  }
  return entry;
}

// Синхронная оценка при монтировании: сообщения, попавшие на экран при открытии чата,
// показываем без ожидания первого коллбэка наблюдателя (он приходит на следующий кадр).
function isNearViewport(node, root) {
  const rect = typeof node.getBoundingClientRect === 'function' ? node.getBoundingClientRect() : null;
  if (!rect) {
    return true;
  }
  const bounds = root
    ? root.getBoundingClientRect()
    : { top: 0, bottom: typeof window !== 'undefined' ? window.innerHeight || 0 : 0 };
  const margin = Math.max(0, bounds.bottom - bounds.top) * NEAR_VIEWPORT_RATIO;
  return rect.bottom >= bounds.top - margin && rect.top <= bounds.bottom + margin;
}

// Этап 31: картинки ленты — ссылки. Сбой сети или 429 от лимита nginx при быстрой
// прокрутке ломали бы картинку насовсем: браузер сам битую картинку не перезапрашивает.
export const LAZY_IMAGE_RETRY_DELAYS_MS = [1500, 5000];

function retryOnError(node, currentSrc) {
  let attempts = 0;
  let timer = null;
  function onError() {
    const src = currentSrc();
    if (!src || src.startsWith('data:') || node.getAttribute('src') !== src || attempts >= LAZY_IMAGE_RETRY_DELAYS_MS.length) {
      return;
    }
    const delay = LAZY_IMAGE_RETRY_DELAYS_MS[attempts];
    attempts += 1;
    timer = setTimeout(() => {
      timer = null;
      if (node.getAttribute('src') === src) {
        node.removeAttribute('src');
        node.src = src;
      }
    }, delay);
  }
  node.addEventListener('error', onError);
  return {
    reset() {
      attempts = 0;
    },
    destroy() {
      node.removeEventListener('error', onError);
      if (timer) {
        clearTimeout(timer);
      }
    },
  };
}

export function lazyImage(node, options = {}) {
  let src = options?.src || '';
  const retry = retryOnError(node, () => src);

  if (typeof IntersectionObserver === 'undefined') {
    node.src = src;
    return {
      update(next = {}) {
        src = next?.src || '';
        retry.reset();
        node.src = src;
      },
      destroy() {
        retry.destroy();
      },
    };
  }

  const root = typeof node.closest === 'function' ? node.closest('[data-scroll-root]') : null;
  const entry = observerFor(root);
  let shown = false;

  function apply(visible) {
    shown = visible;
    const next = visible ? src : LAZY_IMAGE_PLACEHOLDER;
    if (node.getAttribute('src') !== next) {
      node.src = next;
    }
  }

  entry.handlers.set(node, apply);
  entry.observer.observe(node);
  apply(isNearViewport(node, root));

  return {
    update(next = {}) {
      src = next?.src || '';
      retry.reset();
      apply(shown);
    },
    destroy() {
      retry.destroy();
      entry.handlers.delete(node);
      entry.observer.unobserve(node);
    },
  };
}
